import React from 'react';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';

import { Checkbox, ErrorMessage, Input, CurrencyInput } from 'components/HookForm';
import InfoBanner from 'components/InfoBanner/InfoBanner';
import Message from 'components/Message';

import { ICONS } from 'constants/icons';
import { URL_SIMPLE_REGEX } from 'constants/logic';

import { bem } from 'helpers/class-name';

import ProofFileUpload from './ProofFileUpload/ProofFileUpload';
import { messages } from './ServicePricingSection.messages';

const { element } = bem('service-pricing-section');

const PricingInputs = ({
  venue,
  userId,
  locale,
  prefillEnabled,
  proofFileUploadRef,
  servicePriceDescriptionMessage,
  showVerificationBanner,
}) => {
  const { formState } = useFormContext();
  const { errors } = formState;

  const handleOpenPicker = () => proofFileUploadRef.current?.openPicker();

  return (
    <>
      {showVerificationBanner && <InfoBanner icon={ICONS.infoBrand} subtitle={messages.pricingInfoBanner} hideCTA />}

      <CurrencyInput
        name="servicePrice"
        currencyCode={venue.currencyCode}
        locale={locale}
        label={messages.servicePrice}
        required
        showRequiredLabel
        noMargin
        data-cypress="service-price"
      />
      <Message as="p" message={servicePriceDescriptionMessage} className={element('input-description')} />

      <Input
        name="linkToPrice"
        type="text"
        label={messages.linkToPrice}
        labelTooltip={messages.linkToPriceTooltip}
        placeholder={messages.linkToPricePlaceholder}
        pattern={{
          value: URL_SIMPLE_REGEX,
          message: messages.invalidUrl,
        }}
        showRequiredLabel
        noMargin
        data-cypress="link-to-price"
      />
      {/* Link is optional at field level — a file can satisfy proof instead.
          Both partner types get the inline upload trigger. */}
      <p className={element('input-description')}>
        <Message message={messages.linkToPriceDescription} /> <Message message={messages.noLinkUploadPrompt} />{' '}
        <button
          type="button"
          onClick={handleOpenPicker}
          className={element('upload-trigger')}
          data-cypress="proof-file-upload-trigger">
          <Message message={messages.uploadProofLink} />
        </button>
      </p>

      <ProofFileUpload
        ref={proofFileUploadRef}
        venueId={venue.venueId}
        userId={userId}
        prefillEnabled={prefillEnabled}
      />

      <div className="mt+">
        <Checkbox name="accountabilityAgreement" required={messages.accountabilityAgreementRequired}>
          <Message as="span" message={messages.accountabilityAgreementTitle} className="text--semibold" />
          <Message
            as="p"
            message={messages.accountabilityAgreementDescription}
            className={element('input-description')}
          />
        </Checkbox>
        {errors.accountabilityAgreement && <ErrorMessage name="accountabilityAgreement" errors={errors} />}
      </div>
    </>
  );
};

PricingInputs.propTypes = {
  venue: PropTypes.shape({ venueId: PropTypes.number, currencyCode: PropTypes.string }).isRequired,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  locale: PropTypes.string,
  prefillEnabled: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  proofFileUploadRef: PropTypes.shape({ current: PropTypes.any }),
  servicePriceDescriptionMessage: PropTypes.shape({}),
  showVerificationBanner: PropTypes.bool,
};

PricingInputs.defaultProps = {
  showVerificationBanner: true,
};

export default PricingInputs;
